/**
 * Hide and Seek Teams (HNST) types
 *
 * Two fixed teams, Rotworms and Frogdogs, each with one Team Captain. A
 * round's hiding team hides and the other team seeks; who hides comes from the
 * turn order, the admin, a coin toss or a Team Captain's pick (see
 * resolveHnstHidingTeams). A caught hider is out for the rest of the round. Each round is scored
 * Mean-based (the hiding team scores its hiders' average time) or Points-based
 * (the seeking team scores seconds for each hider it found, and the hiding team
 * may score them for each hider who survived); see HnstRoundScoring. A team's
 * game total is the sum of its round scores.
 *
 * Prefix: "Hnst"
 */
export const HNST_TEAM_KEYS = ['rotworms', 'frogdogs'];
export const HNST_TEAMS = {
    rotworms: { key: 'rotworms', name: 'Rotworms', hue: 'rotworm', discordRoleColor: '#ac27ff' },
    frogdogs: { key: 'frogdogs', name: 'Frogdogs', hue: 'frogdog', discordRoleColor: '#ffdd14' },
};
/** The team that isn't this one. */
export function otherHnstTeam(team) {
    return team === 'rotworms' ? 'frogdogs' : 'rotworms';
}
export function isHnstTeamKey(value) {
    return value === 'rotworms' || value === 'frogdogs';
}
/** The team a player is on in a Hide and Seek Teams game, or null when they are on neither. */
export function getHnstTeamOf(game, playerId) {
    return game.hnstTeams?.find((t) => t.memberIds.includes(playerId))?.key ?? null;
}
/** The team a player captains, or null. */
export function getHnstCaptainTeam(game, playerId) {
    return game.hnstTeams?.find((t) => t.captainPlayerId === playerId)?.key ?? null;
}
/**
 * What still stops a Hide and Seek Teams game going from team creation to the
 * lobby: each team needs a captain, the draft must not be running, and every
 * enabled player must be on a team. Empty when ready. The server and the admin
 * client both check it.
 */
export function getHnstTeamsNotReady(game) {
    const missing = [];
    for (const key of HNST_TEAM_KEYS) {
        const team = game.hnstTeams?.find((t) => t.key === key);
        if (!team?.captainPlayerId)
            missing.push(`${HNST_TEAMS[key].name} — no Team Captain`);
    }
    if (game.hnstDraft?.status === 'in_progress')
        missing.push('The draft is still running');
    for (const player of game.players) {
        if (player.enabled === false || player.status === 'left')
            continue;
        if (!getHnstTeamOf(game, player.playerId)) {
            missing.push(`${player.characterName ?? `Player ${player.playerId.slice(-4)}`} — not on a team`);
        }
    }
    return missing;
}
// ============================================================================
// Snake draft
// ============================================================================
/** Seconds a captain has for each pick. */
export const HNST_DRAFT_PICK_SECONDS_DEFAULT = 30;
export const HNST_DRAFT_PICK_SECONDS_MIN = 10;
export const HNST_DRAFT_PICK_SECONDS_MAX = 120;
export const DEFAULT_HNST_DRAFT_CONFIG = {
    timePerPickSeconds: HNST_DRAFT_PICK_SECONDS_DEFAULT,
};
/**
 * How long the coin spins before it lands. The clients' spin is a CSS animation
 * of the same length ($hnst-coin-spin-time in platform/ui's games/hnst/_coin.scss),
 * and the Cinematics overlay's 3D coin lands at `landsAt`: change both together.
 */
export const HNST_COIN_FLIP_SPIN_MS = 3500;
/** Seconds the winning captain has to choose once the coin lands; then the server chooses at random. */
export const HNST_COIN_CHOICE_SECONDS = 30;
export const HNST_COIN_TOSS_KIND_LABELS = {
    rounds: 'Who hides',
    picks: 'Team Captain picks',
};
export const HNST_COIN_TOSS_MODE_LABELS = {
    winner_hides: 'Winner hides first',
    winner_chooses: 'Winning Team Captain chooses',
};
/**
 * The team a toss sends to hide first (in a picks toss: whose pick plays
 * first), or null until it is decided and, given `now` (server ms), landed.
 */
export function hnstTossFirstHidingTeam(toss, now = Infinity) {
    if (!toss.flip || !toss.choice || now < toss.flip.landsAt)
        return null;
    return toss.choice === 'hide' ? toss.flip.winner : otherHnstTeam(toss.flip.winner);
}
/** "Coin toss 2": the toss's place in the game's list. */
export function hnstCoinTossLabel(game, tossId) {
    const index = (game.hnstCoinTosses ?? []).findIndex((t) => t.id === tossId);
    return index < 0 ? 'Coin toss' : `Coin toss ${index + 1}`;
}
/** The indexes of the rounds linked to a toss, in round order. */
export function hnstTossRoundIndexes(game, tossId) {
    const indexes = [];
    game.rounds.forEach((round, index) => {
        if (round.settings.gameType === 'hnst' && round.settings.coinTossId === tossId)
            indexes.push(index);
    });
    return indexes;
}
/**
 * Why a toss can't be flipped, chosen or changed now, or null when it can: one
 * of its rounds is loaded, or has begun.
 */
export function hnstTossLockedReason(game, tossId) {
    for (const index of hnstTossRoundIndexes(game, tossId)) {
        if (game.rounds[index].status !== 'setup')
            return `Round ${index + 1} has been played`;
        if (game.loadedRoundIndex === index)
            return `Round ${index + 1} is loaded: unload it first`;
    }
    return null;
}
// ============================================================================
// Countdowns (the Cinematics overlay)
// ============================================================================
/** Seconds counted down before a round's hiding begins; 0 starts hiding at once. */
export const HNST_PRE_ROUND_COUNTDOWN_DEFAULT = 5;
export const HNST_PRE_ROUND_COUNTDOWN_MIN = 0;
export const HNST_PRE_ROUND_COUNTDOWN_MAX = 15;
/**
 * The last seconds of hiding, and of seeking, the players' Cinematics overlay
 * counts down big in the middle of the screen; 0 shows only the words at zero.
 */
export const HNST_FINAL_COUNTDOWN_DEFAULT = 5;
export const HNST_FINAL_COUNTDOWN_MIN = 0;
export const HNST_FINAL_COUNTDOWN_MAX = 10;
export const DEFAULT_HNST_COUNTDOWN_CONFIG = {
    preRoundSeconds: HNST_PRE_ROUND_COUNTDOWN_DEFAULT,
    finalSeconds: HNST_FINAL_COUNTDOWN_DEFAULT,
};
/** The game's countdowns, or the defaults when the admin hasn't set them. */
export function getHnstCountdownConfig(game) {
    return { ...DEFAULT_HNST_COUNTDOWN_CONFIG, ...game.hnstCountdownConfig };
}
/** Why these countdowns aren't allowed, or null when they are. */
export function validateHnstCountdownConfig(config) {
    const check = (value, min, max, label) => typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max
        ? `${label} must be a whole number of seconds from ${min} to ${max}`
        : null;
    return (check(config.preRoundSeconds, HNST_PRE_ROUND_COUNTDOWN_MIN, HNST_PRE_ROUND_COUNTDOWN_MAX, 'The pre-round countdown') ??
        check(config.finalSeconds, HNST_FINAL_COUNTDOWN_MIN, HNST_FINAL_COUNTDOWN_MAX, 'The final countdown'));
}
/**
 * Who hides in each round of a Hide and Seek Teams game, in round order. A
 * round that has begun keeps the team it had. Otherwise a Team Captain picked
 * round is its owner's, a fixed team is the admin's, and a round linked to a
 * "who hides" coin toss takes the toss's first team, then the teams take turns
 * over the toss's rounds. Any other round goes to the other team from the round
 * before it. An abandoned or disabled round doesn't use up its team's turn.
 *
 * `now` (server ms): a toss whose coin is still in the air hasn't decided yet.
 */
export function resolveHnstHidingTeams(game, now = Infinity) {
    const tosses = new Map((game.hnstCoinTosses ?? []).map((toss) => [toss.id, toss]));
    // The team of the latest round that used its turn (null: not decided).
    let lastTeam = null;
    const lastTossTeam = new Map();
    return game.rounds.map((round) => {
        const settings = round.settings;
        if (settings.gameType !== 'hnst')
            return { team: null, source: 'turn', coinTossId: null };
        const coinTossId = settings.coinTossId ?? null;
        const toss = coinTossId !== null ? tosses.get(coinTossId) : undefined;
        let resolved;
        if (round.status !== 'setup' && settings.hidingTeam) {
            resolved = { team: settings.hidingTeam, source: 'played', coinTossId };
        }
        else if (settings.ownerTeam) {
            resolved = { team: settings.ownerTeam, source: 'pick', coinTossId };
        }
        else if (toss && toss.kind === 'rounds') {
            const previous = lastTossTeam.get(toss.id);
            const team = previous !== undefined ? previous && otherHnstTeam(previous) : hnstTossFirstHidingTeam(toss, now);
            resolved = { team, source: 'toss', coinTossId };
        }
        else if (settings.hidingTeam) {
            resolved = { team: settings.hidingTeam, source: 'fixed', coinTossId };
        }
        else {
            resolved = { team: lastTeam && otherHnstTeam(lastTeam), source: 'turn', coinTossId };
        }
        if (round.status !== 'abandoned' && !round.disabled) {
            lastTeam = resolved.team;
            if (toss?.kind === 'rounds')
                lastTossTeam.set(toss.id, resolved.team);
        }
        return resolved;
    });
}
/**
 * The round to start next: the loaded one, else the first in setup (not
 * disabled). Null when none is left.
 */
export function hnstNextRoundIndex(game) {
    if (game.loadedRoundIndex !== undefined && game.rounds[game.loadedRoundIndex]?.status === 'setup') {
        return game.loadedRoundIndex;
    }
    const index = game.rounds.findIndex((round) => round.status === 'setup' && !round.disabled);
    return index < 0 ? null : index;
}
/** The team that hides in the next round to start (see hnstNextRoundIndex), or null when it isn't decided. */
export function getNextHnstHidingTeam(game, now = Infinity) {
    const index = hnstNextRoundIndex(game);
    return index === null ? null : resolveHnstHidingTeams(game, now)[index].team;
}
/**
 * A Team Captain picked round this viewer may not see yet: still in setup,
 * not revealed (the admin loads it to reveal it), and another team's pick.
 * Admins see every round.
 */
export function isHnstPickHiddenFrom(round, viewerTeam) {
    const settings = round.settings;
    if (settings.gameType !== 'hnst' || !settings.ownerTeam)
        return false;
    if (round.status !== 'setup' || settings.revealedAt)
        return false;
    return settings.ownerTeam !== viewerTeam;
}
/**
 * The moments an admin can preview on one player's Cinematics overlay, with
 * sample data from the room: each moment, and two that test the layout (a
 * burst of catches rolling up, and catches docking under a countdown).
 */
export const HNST_STAGE_PREVIEW_KINDS = [
    'coin',
    'pre-round',
    'round-intro',
    'final-hiding',
    'final-seeking',
    'round-over',
    'game-over',
    'awards',
    'results-link',
    'caught-self',
    'caught-catcher',
    'caught-teammate',
    'caught-other',
    'caught-burst',
    'caught-during-countdown',
];
export function isHnstStagePreviewKind(value) {
    return typeof value === 'string' && HNST_STAGE_PREVIEW_KINDS.includes(value);
}
